import { lazy, Suspense, useEffect } from 'react';
import type { ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './store/auth';
import { useTheme } from './store/theme';
import { ErrorBoundary } from './components/ErrorBoundary';

const LoginPage = lazy(() => import('./pages/Login').then((m) => ({ default: m.LoginPage })));
const DashboardPage = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.DashboardPage })));
const FilesPage = lazy(() => import('./pages/Files').then((m) => ({ default: m.FilesPage })));
const NotificationsPage = lazy(() => import('./pages/Notifications').then((m) => ({ default: m.NotificationsPage })));
const SettingsPage = lazy(() => import('./pages/Settings').then((m) => ({ default: m.SettingsPage })));
const SharesPage = lazy(() => import('./pages/Shares').then((m) => ({ default: m.SharesPage })));
const BookmarksPage = lazy(() => import('./pages/Bookmarks').then((m) => ({ default: m.BookmarksPage })));
const MediaPage = lazy(() => import('./pages/Media').then((m) => ({ default: m.MediaPage })));
const UsersAdminPage = lazy(() => import('./pages/admin/Users').then((m) => ({ default: m.UsersAdminPage })));
const AdminProxmoxPage = lazy(() => import('./pages/admin/Proxmox').then((m) => ({ default: m.AdminProxmoxPage })));
const AdminLogAggregatorPage = lazy(() => import('./pages/admin/LogAggregator').then((m) => ({ default: m.AdminLogAggregatorPage })));
const AdminDevOpsPage = lazy(() => import('./pages/admin/DevOps').then((m) => ({ default: m.AdminDevOpsPage })));
const AdminAlertsPage = lazy(() => import('./pages/admin/Alerts').then((m) => ({ default: m.AdminAlertsPage })));
const AdminIntegrationsPage = lazy(() => import('./pages/admin/Integrations').then((m) => ({ default: m.AdminIntegrationsPage })));
const PastebinPage = lazy(() => import('./pages/Pastebin').then((m) => ({ default: m.PastebinPage })));
const PasteViewPage = lazy(() => import('./pages/PasteView').then((m) => ({ default: m.PasteViewPage })));
const HealthPage = lazy(() => import('./pages/Health').then((m) => ({ default: m.HealthPage })));
const StatsPage = lazy(() => import('./pages/Stats').then((m) => ({ default: m.StatsPage })));
const IncidentsPage = lazy(() => import('./pages/Incidents').then((m) => ({ default: m.IncidentsPage })));
const TvModePage = lazy(() => import('./pages/TvMode').then((m) => ({ default: m.TvModePage })));
const TimelinePage = lazy(() => import('./pages/Timeline').then((m) => ({ default: m.TimelinePage })));
const AuditPage = lazy(() => import('./pages/admin/Audit').then((m) => ({ default: m.AuditPage })));
const AdminInfraConfigPage = lazy(() => import('./pages/admin/InfraConfig').then((m) => ({ default: m.AdminInfraConfigPage })));
const AdminServicesPage = lazy(() => import('./pages/admin/Services').then((m) => ({ default: m.AdminServicesPage })));
const AdminTokensPage = lazy(() => import('./pages/admin/Tokens').then((m) => ({ default: m.AdminTokensPage })));
const AdminForwardersPage = lazy(() => import('./pages/admin/Forwarders').then((m) => ({ default: m.AdminForwardersPage })));
const AdminTrashPage = lazy(() => import('./pages/admin/Trash').then((m) => ({ default: m.AdminTrashPage })));
const AdminActivityPage = lazy(() => import('./pages/admin/Activity').then((m) => ({ default: m.AdminActivityPage })));
const AdminSettingsPage = lazy(() => import('./pages/admin/Settings').then((m) => ({ default: m.AdminSettingsPage })));
const AdminWebhooksPage = lazy(() => import('./pages/admin/Webhooks').then((m) => ({ default: m.AdminWebhooksPage })));
const AdminContainersPage = lazy(() => import('./pages/admin/Containers').then((m) => ({ default: m.AdminContainersPage })));
const AdminSslPage = lazy(() => import('./pages/admin/Ssl').then((m) => ({ default: m.AdminSslPage })));
const AdminLogsPage = lazy(() => import('./pages/admin/Logs').then((m) => ({ default: m.AdminLogsPage })));
const PublicStatusPage = lazy(() => import('./pages/PublicStatus').then((m) => ({ default: m.PublicStatusPage })));
const MagicLoginPage = lazy(() => import('./pages/MagicLogin').then((m) => ({ default: m.MagicLoginPage })));
const KanbanPage = lazy(() => import('./pages/Kanban').then((m) => ({ default: m.KanbanPage })));
const NotesPage = lazy(() => import('./pages/Notes').then((m) => ({ default: m.NotesPage })));
const AdminAutomationPage = lazy(() => import('./pages/admin/Automation').then((m) => ({ default: m.AdminAutomationPage })));
const AdminSchedulerPage = lazy(() => import('./pages/admin/Scheduler').then((m) => ({ default: m.AdminSchedulerPage })));
const AdminFail2banPage = lazy(() => import('./pages/admin/Fail2ban').then((m) => ({ default: m.AdminFail2banPage })));
const AdminSessionMapPage = lazy(() => import('./pages/admin/SessionMap').then((m) => ({ default: m.AdminSessionMapPage })));
const AdminNetworkToolsPage = lazy(() => import('./pages/admin/NetworkTools').then((m) => ({ default: m.AdminNetworkToolsPage })));
const AdminOpenClawPage = lazy(() => import('./pages/admin/OpenClaw').then((m) => ({ default: m.AdminOpenClawPage })));
const AdminPushNotificationsPage = lazy(() => import('./pages/admin/PushNotifications').then((m) => ({ default: m.AdminPushNotificationsPage })));
const Layout = lazy(() => import('./components/Layout').then((m) => ({ default: m.Layout })));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="size-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4">
      <h1 className="text-4xl font-bold">404</h1>
      <p className="text-muted-foreground">Página não encontrada</p>
      <a href="/" className="text-primary underline">Voltar ao início</a>
    </div>
  );
}

function Protected({ children, admin }: { children: ReactNode; admin?: boolean }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-muted-foreground">A carregar…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  const init = useAuth((s) => s.init);
  const themeInit = useTheme((s) => s.init);
  useEffect(() => {
    themeInit();
    init();
  }, [init, themeInit]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/status" element={<PublicStatusPage />} />
          <Route path="/p/:token" element={<PasteViewPage />} />
          <Route path="/magic/:token" element={<MagicLoginPage />} />
          <Route
            path="/tv"
            element={
              <Protected>
                <TvModePage />
              </Protected>
            }
          />
          <Route
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="/files" element={<FilesPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/shares" element={<SharesPage />} />
            <Route path="/bookmarks" element={<BookmarksPage />} />
            <Route path="/media" element={<MediaPage />} />
            <Route path="/health/:serviceId" element={<HealthPage />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/incidents" element={<IncidentsPage />} />
            <Route path="/timeline" element={<TimelinePage />} />
            <Route path="/pastes" element={<PastebinPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/kanban" element={<KanbanPage />} />
            <Route path="/notes" element={<NotesPage />} />
            <Route
              path="/admin/users"
              element={<Protected admin><UsersAdminPage /></Protected>}
            />
            <Route
              path="/admin/infra"
              element={<Protected admin><AdminInfraConfigPage /></Protected>}
            />
            <Route path="/admin/services" element={<Navigate to="/admin/infra?tab=services" replace />} />
            <Route path="/admin/storage" element={<Navigate to="/admin/infra?tab=storage" replace />} />
            <Route
              path="/admin/tokens"
              element={<Protected admin><AdminTokensPage /></Protected>}
            />
            <Route
              path="/admin/forwarders"
              element={<Protected admin><AdminForwardersPage /></Protected>}
            />
            <Route
              path="/admin/webhooks"
              element={<Protected admin><AdminWebhooksPage /></Protected>}
            />
            <Route path="/admin/backup-watchers" element={<Navigate to="/admin/infra?tab=watchers" replace />} />
            <Route path="/admin/backups" element={<Navigate to="/admin/infra?tab=backups" replace />} />
            <Route
              path="/admin/containers"
              element={<Protected admin><AdminContainersPage /></Protected>}
            />
            <Route
              path="/admin/proxmox"
              element={<Protected admin><AdminProxmoxPage /></Protected>}
            />
            <Route path="/admin/media" element={<Navigate to="/admin/integrations?tab=media" replace />} />
            <Route
              path="/admin/integrations"
              element={<Protected admin><AdminIntegrationsPage /></Protected>}
            />
            <Route
              path="/admin/log-aggregator"
              element={<Protected admin><AdminLogAggregatorPage /></Protected>}
            />
            <Route
              path="/admin/devops"
              element={<Protected admin><AdminDevOpsPage /></Protected>}
            />
            <Route
              path="/admin/alerts"
              element={<Protected admin><AdminAlertsPage /></Protected>}
            />
            <Route
              path="/admin/ssl"
              element={<Protected admin><AdminSslPage /></Protected>}
            />
            <Route
              path="/admin/logs"
              element={<Protected admin><AdminLogsPage /></Protected>}
            />
            <Route
              path="/admin/trash"
              element={<Protected admin><AdminTrashPage /></Protected>}
            />
            <Route
              path="/admin/activity"
              element={<Protected admin><AdminActivityPage /></Protected>}
            />
            <Route
              path="/admin/settings"
              element={<Protected admin><AdminSettingsPage /></Protected>}
            />
            <Route
              path="/admin/audit"
              element={<Protected admin><AuditPage /></Protected>}
            />
            <Route
              path="/admin/automation"
              element={<Protected admin><AdminAutomationPage /></Protected>}
            />
            <Route
              path="/admin/scheduler"
              element={<Protected admin><AdminSchedulerPage /></Protected>}
            />
            <Route
              path="/admin/fail2ban"
              element={<Protected admin><AdminFail2banPage /></Protected>}
            />
            <Route
              path="/admin/session-map"
              element={<Protected admin><AdminSessionMapPage /></Protected>}
            />
            <Route
              path="/admin/network-tools"
              element={<Protected admin><AdminNetworkToolsPage /></Protected>}
            />
            <Route
              path="/admin/openclaw"
              element={<Protected admin><AdminOpenClawPage /></Protected>}
            />
            <Route
              path="/admin/push"
              element={<Protected admin><AdminPushNotificationsPage /></Protected>}
            />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
